import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApiService } from './api.service';
import { UtilityService } from './utility.service';
import { ActiveHospitalService } from './active-hospital.service';
import { HospitalSettingsService } from './hospital-settings.service';
import { ReleaseNotesService, ReleaseNote } from './release-notes.service';
import { HospitalSiteSelectorComponent } from '../components/_shared/modals/hospital-site-selector/hospital-site-selector.component';
import { HospitalGroupEmailDialogComponent } from '../components/modules/pcv/components/vc-my-profile/modal/hospital-group-email-dialog/hospital-group-email-dialog.component';
import { ReleaseNotesPopupComponent } from '../components/_shared/support/release-notes-popup/release-notes-popup.component';

type LayerKey = 'site' | 'email' | 'notes';

interface SitesFetchResult {
  applicable: boolean;
  sites: any[];
}

interface EmailFetchResult {
  applicable: boolean;
  settings: any;
}

interface ReleaseNotesFetchResult {
  count: number;
  releaseNotes: ReleaseNote[];
}

/**
 * Orchestrates the three independent hospital-user post-login pop-ups —
 * hospital site selection, hospital group email configuration, and
 * targeted release notes — so that whenever more than one applies for the
 * current user/session, they are opened TOGETHER as layered/stacked
 * dialogs sharing a single backdrop, rather than as separate, independently
 * triggered interruptions.
 *
 * Fixed front-to-back order (per product decision): site selection (front),
 * hospital group email (middle), release notes (back). Angular CDK overlays
 * all share the same base wrapper z-index, so relative stacking between
 * separate dialogs is controlled by DOM append order (later-opened renders
 * on top) — `evaluateAndOpen` opens the back-most applicable dialog first
 * and the front-most last, so any subset of the three can be shown while
 * preserving the same relative front-to-back order. Only the back-most dialog among those actually opened
 * keeps a visible backdrop (`hasBackdrop: true`); the others render with
 * `hasBackdrop: false` so only one darkened scrim is ever visible at once,
 * and it naturally persists until the whole stack is dismissed (the user
 * can only interact with dialogs in front-to-back order because each front
 * layer fully occupies the same centered position as the ones behind it).
 *
 * When more than one dialog is shown, `disableClose` is forced to `true` on
 * all of them (blocking ESC/backdrop-click dismissal) so a stray backdrop
 * click or ESC press cannot accidentally close a layer that isn't visibly
 * on top — each dialog's own explicit close/skip/submit controls are
 * unaffected by `disableClose` and remain the only way to progress through
 * the stack. When only a single dialog applies, its original standalone
 * `hasBackdrop`/`disableClose` behavior is preserved unchanged.
 *
 * Site selection and hospital email each self-guard via a per-login
 * sessionStorage flag so they are shown at most once per browser
 * session/login (cleared on logout, see `ApiService.logout()`), matching
 * the "every login, not every page nav" behavior requested by the user.
 * Release notes keeps its pre-existing behavior of re-appearing later in
 * the same session if the unread count increases (e.g. a new note is
 * published mid-session).
 */
@Injectable({
  providedIn: 'root'
})
export class PostLoginHospitalPopupsService {

  private siteSelectionDialogRef: MatDialogRef<HospitalSiteSelectorComponent> | null = null;
  private hospitalEmailDialogRef: MatDialogRef<HospitalGroupEmailDialogComponent> | null = null;
  private releaseNotesDialogRef: MatDialogRef<ReleaseNotesPopupComponent> | null = null;
  private checkInProgress = false;

  constructor(
    private dialog: MatDialog,
    private api: ApiService,
    private router: Router,
    private utility: UtilityService,
    private activeHospitalService: ActiveHospitalService,
    private hospitalSettingsService: HospitalSettingsService,
    private releaseNotesService: ReleaseNotesService
  ) {}

  /**
   * Evaluates all three conditions for the given user and opens whichever
   * apply, layered together. Safe to call repeatedly (e.g. on every
   * dashboard-route navigation) — it is a no-op while a check is already in
   * flight or while any of the three dialogs is still open.
   */
  checkAndShowPopups(userId: string): void {
    if (!userId || this.checkInProgress || this.anyLayerOpen()) {
      return;
    }

    const userType = localStorage.getItem('endUserType');
    if (userType !== 'pcv' && userType !== 'vc') {
      return;
    }

    this.checkInProgress = true;

    forkJoin({
      sitesResult: this.fetchSitesIfNeeded(userId),
      emailResult: this.fetchEmailSettingsIfNeeded(),
      releaseNotesResult: this.releaseNotesService.getUnreadReleaseNotes().pipe(
        catchError(() => of({ count: 0, releaseNotes: [] as ReleaseNote[] }))
      )
    }).subscribe(({ sitesResult, emailResult, releaseNotesResult }) => {
      this.checkInProgress = false;
      this.evaluateAndOpen(userId, sitesResult, emailResult, releaseNotesResult);
    });
  }

  private anyLayerOpen(): boolean {
    return !!(this.siteSelectionDialogRef || this.hospitalEmailDialogRef || this.releaseNotesDialogRef);
  }

  private fetchSitesIfNeeded(userId: string): Observable<SitesFetchResult> {
    if (this.wasShownThisLogin(this.siteSelectionKey(userId))) {
      return of({ applicable: false, sites: [] });
    }
    return this.api.apiRequest('get', 'hospital-access/my-sites', {}).pipe(
      map((res: any) => ({ applicable: true, sites: res?.data || [] })),
      catchError(() => of({ applicable: false, sites: [] }))
    );
  }

  private fetchEmailSettingsIfNeeded(): Observable<EmailFetchResult> {
    const hospitalRole = localStorage.getItem('hospitalRole');
    const hospitalCode = localStorage.getItem('hospitalCode');
    const userId = localStorage.getItem('endUserId') || '';

    if (hospitalRole !== 'admin' || !hospitalCode || this.wasShownThisLogin(this.hospitalEmailKey(userId))) {
      return of({ applicable: false, settings: null });
    }

    return this.hospitalSettingsService.getSettings(hospitalCode).pipe(
      map((res: any) => ({ applicable: true, settings: res?.data?.settings || null })),
      catchError(() => of({ applicable: false, settings: null }))
    );
  }

  private evaluateAndOpen(
    userId: string,
    sitesResult: SitesFetchResult,
    emailResult: EmailFetchResult,
    releaseNotesResult: ReleaseNotesFetchResult
  ): void {
    const hospitalCode = localStorage.getItem('hospitalCode') || '';
    const practiceName = localStorage.getItem('practiceName') || '';

    const grantedSites = (sitesResult.sites || []).filter((s: any) => s.status === 'granted');
    const needsSiteSelection = sitesResult.applicable && grantedSites.length > 1;
    const needsEmailConfig = emailResult.applicable && (!emailResult.settings || !emailResult.settings.decisionMade);
    const showReleaseNotes = this.shouldShowReleaseNotes(userId, releaseNotesResult.count);

    const layers: LayerKey[] = [];
    if (needsSiteSelection) { layers.push('site'); }
    if (needsEmailConfig) { layers.push('email'); }
    if (showReleaseNotes) { layers.push('notes'); }

    if (layers.length === 0) {
      return;
    }

    const stacked = layers.length > 1;
    const backmost = layers[layers.length - 1];

    // Angular CDK overlays all share the same base z-index (1000, set on
    // each dialog's own `.cdk-global-overlay-wrapper`), so `panelClass`
    // z-index overrides on `.cdk-overlay-pane` alone can't reorder dialogs
    // relative to EACH OTHER (each wrapper establishes its own stacking
    // context). Cross-dialog front-to-back order is therefore controlled by
    // DOM append order instead: CDK paints later-opened overlays on top, so
    // we open in the REVERSE of the desired visual order (back-most first,
    // front-most last) — release notes, then hospital email, then site
    // selection — so the canonical front-to-back order (site selection,
    // hospital email, release notes) is what the user actually sees.
    if (showReleaseNotes) {
      this.openReleaseNotes(releaseNotesResult, backmost === 'notes', stacked ? true : false);
      this.setReleaseNotesPopupShownCount(userId, releaseNotesResult.count);
    }

    if (needsEmailConfig) {
      this.openHospitalEmail(hospitalCode, practiceName, backmost === 'email', true /* dialog's own default is already true */);
      sessionStorage.setItem(this.hospitalEmailKey(userId), 'true');
    }

    if (needsSiteSelection) {
      this.openSiteSelection(sitesResult.sites, backmost === 'site', stacked ? true : false);
      sessionStorage.setItem(this.siteSelectionKey(userId), 'true');
    }
  }

  private openSiteSelection(sites: any[], hasBackdrop: boolean, disableClose: boolean): void {
    // Keep the shared multi-site state in sync, same as PcvComponent's own
    // refresh-time initialization, so the navbar switcher and other
    // consumers of ActiveHospitalService see the latest site list.
    this.activeHospitalService.initialize(sites);

    this.siteSelectionDialogRef = this.dialog.open(HospitalSiteSelectorComponent, {
      width: '520px',
      panelClass: 'popup-layer-site-selection',
      hasBackdrop,
      disableClose,
      data: { sites: this.activeHospitalService.getAllSites() }
    });

    this.siteSelectionDialogRef.afterClosed().subscribe((result: any) => {
      this.siteSelectionDialogRef = null;
      if (result && result.action === 'switched') {
        // Trigger page reload to refresh data for the newly selected hospital
        // context (matches existing PcvComponent.openSiteSelector behavior).
        const currentUrl = this.router.url;
        this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
          this.router.navigate([currentUrl]);
        });
      }
    });
  }

  private openHospitalEmail(hospitalCode: string, practiceName: string, hasBackdrop: boolean, disableClose: boolean): void {
    this.hospitalEmailDialogRef = this.dialog.open(HospitalGroupEmailDialogComponent, {
      width: '500px',
      panelClass: 'popup-layer-hospital-email',
      hasBackdrop,
      disableClose,
      data: { hospitalCode, practiceName }
    });

    this.hospitalEmailDialogRef.afterClosed().subscribe((result: any) => {
      this.hospitalEmailDialogRef = null;
      if (result && result.action) {
        this.hospitalSettingsService.configureEmail({
          hospitalCode,
          action: result.action,
          email: result.email
        }).subscribe(
          () => {
            if (result.action === 'yes') {
              this.utility.openSnackBar('Hospital group email configured successfully.', 'Ok');
            } else if (result.action === 'no') {
              this.utility.openSnackBar('Hospital group email declined.', 'Ok');
            }
          },
          (err: any) => {
            console.error('Error configuring hospital email:', err);
            this.utility.openSnackBar('Failed to save your selection. Please try again.', 'Ok');
          }
        );
      }
    });
  }

  private openReleaseNotes(data: ReleaseNotesFetchResult, hasBackdrop: boolean, disableClose: boolean): void {
    this.releaseNotesDialogRef = this.dialog.open(ReleaseNotesPopupComponent, {
      width: '560px',
      maxWidth: '92vw',
      panelClass: 'popup-layer-release-notes',
      hasBackdrop,
      disableClose,
      data: { releaseNotes: data.releaseNotes }
    });

    this.releaseNotesDialogRef.afterClosed().subscribe(() => {
      this.releaseNotesDialogRef = null;
    });
  }

  private siteSelectionKey(userId: string): string {
    return `siteSelectionPopupShownForLogin_${userId}`;
  }

  private hospitalEmailKey(userId: string): string {
    return `hospitalEmailPopupShownForLogin_${userId}`;
  }

  private wasShownThisLogin(key: string): boolean {
    return sessionStorage.getItem(key) === 'true';
  }

  private releaseNotesPopupCountStorageKey(userId: string): string {
    // Same key format previously used in app.component.ts — ownership of
    // the pcv/vc release-notes check moves to this service, but the
    // session-scoping semantics (and key) are unchanged.
    return `releaseNotesPopupShownCount_${userId}`;
  }

  private getReleaseNotesPopupShownCount(userId: string): number | null {
    const raw = sessionStorage.getItem(this.releaseNotesPopupCountStorageKey(userId));
    return raw !== null ? parseInt(raw, 10) : null;
  }

  private setReleaseNotesPopupShownCount(userId: string, count: number): void {
    sessionStorage.setItem(this.releaseNotesPopupCountStorageKey(userId), String(count));
  }

  private shouldShowReleaseNotes(userId: string, count: number): boolean {
    if (!count) {
      return false;
    }
    const lastShown = this.getReleaseNotesPopupShownCount(userId);
    if (lastShown !== null && count <= lastShown) {
      return false;
    }
    return true;
  }
}
